import type {
  ChangeSetFileContent,
  GitCommitResult,
  GitDiscardResult,
  GitIdentity,
  GitInstallPlan,
  GitInstallResult,
  GitPrepareResult,
  GitReadiness,
  GitStatusCheck,
} from "@racler/protocol";

/**
 * The shared Git Setup architecture. Git itself is only ever inspected/changed by the Machine Agent; this module
 * just drives the typed requests and decides the NEXT setup step from the readiness state. React screens
 * (Autonomous selection, run start, Create/Add Project) all use it, none of them checks Git on its own.
 */
export interface GitSetupApi {
  check(): Promise<GitReadiness>;
  status?(): Promise<GitStatusCheck>;
  diff?(): Promise<{ files: ChangeSetFileContent[] }>;
  installPlan(): Promise<GitInstallPlan>;
  install(): Promise<GitInstallResult>;
  prepare(opts: { identity?: GitIdentity }): Promise<GitPrepareResult>;
  commit?(opts: { message: string; identity?: GitIdentity }): Promise<GitCommitResult>;
  discard?(): Promise<GitDiscardResult>;
}

/** Where a Project's Git setup stands, in product terms. */
export type GitSetupStep = "done" | "install" | "prepare" | "baseline" | "error";

export function stepFor(readiness: GitReadiness): GitSetupStep {
  switch (readiness.state) {
    case "READY":
      return "done";
    case "GIT_NOT_INSTALLED":
      return "install";
    case "NOT_A_REPOSITORY":
      return "prepare";
    case "NO_HEAD":
      return "baseline";
    default:
      return "error";
  }
}

export const isReady = (r: GitReadiness | null | undefined): boolean => r?.state === "READY";

/** Product wording per state (no Worktree implementation details). */
export const GIT_SETUP_COPY = {
  why: "Agent Autonomous uses Git to isolate and safely apply its changes.",
  install: { title: "Git setup required", body: "Git is not available on this machine.", action: "Install Git" },
  prepare: {
    title: "Git setup required",
    body: "This project needs Git before Agent Autonomous can safely run.",
    action: "Prepare Project",
  },
  baseline: {
    title: "Git setup required",
    body: "This Git repository does not have an initial baseline yet.",
    action: "Create Baseline",
  },
  error: { title: "Git check failed", body: "Git readiness could not be determined.", action: "Check again" },
} as const;

/** What preparing / baselining does, shown for explicit confirmation before touching an existing folder. */
export const PREPARE_EXPLANATION: Record<"prepare" | "baseline", string> = {
  prepare:
    "racler will run git init in this project folder and save one local starting commit of its current files (respecting .gitignore). Nothing is pushed, no remote is added, and your global Git settings are not changed.",
  baseline:
    "racler will save one local starting commit of this project's current files (respecting .gitignore). Nothing is pushed, no remote is added, and your global Git settings are not changed.",
};

export async function safeCheck(api: GitSetupApi): Promise<GitReadiness> {
  try {
    return await api.check();
  } catch {
    // Fail closed: an unreachable Machine or a failed request is never READY.
    return { state: "ERROR", message: "Git readiness could not be determined." };
  }
}

/**
 * Decision made when the user picks Agent Autonomous (and again right before a run starts):
 * READY → proceed; anything else → open the Git Setup flow (the mode must NOT switch yet).
 */
export type AutonomousGate = { kind: "ready" } | { kind: "setup"; readiness: GitReadiness };

export async function gateAutonomous(api: GitSetupApi): Promise<AutonomousGate> {
  const readiness = await safeCheck(api);
  return isReady(readiness) ? { kind: "ready" } : { kind: "setup", readiness };
}
export type PreflightResult =
  | { kind: "ready" }
  | { kind: "setup"; readiness: GitReadiness }
  | { kind: "unsaved"; dirtyPaths: string[] }
  | { kind: "dirty"; changedCount: number; changedFiles?: string[] };

/**
 * Autonomous preflight run before starting an Agent Autonomous run.
 * 1. Check unsaved Editor buffers.
 * 2. Check Git readiness (installed, is repo, has HEAD).
 * 3. Check Git working-tree status (clean).
 */
export async function checkAutonomousPreflight(opts: {
  api: GitSetupApi;
  dirtyPaths: string[];
}): Promise<PreflightResult> {
  if (opts.dirtyPaths.length > 0) {
    return { kind: "unsaved", dirtyPaths: opts.dirtyPaths };
  }

  const readiness = await safeCheck(opts.api);
  if (!isReady(readiness)) {
    return { kind: "setup", readiness };
  }

  if (opts.api.status) {
    try {
      const st = await opts.api.status();
      if (!st.isClean) {
        return {
          kind: "dirty",
          changedCount: st.changedCount ?? st.changedFiles?.length ?? 1,
          changedFiles: st.changedFiles,
        };
      }
    } catch {
      return { kind: "setup", readiness: { state: "ERROR", message: "Failed to inspect git status." } };
    }
  }

  return { kind: "ready" };
}

export async function runCommit(
  api: GitSetupApi,
  message: string,
  identity?: GitIdentity,
): Promise<{ ok: boolean; needsIdentity?: boolean; error?: string }> {
  if (!api.commit) return { ok: false, error: "Commit API not available" };
  try {
    const res = await api.commit({ message, identity });
    if (res.needsIdentity) return { ok: false, needsIdentity: true };
    if (!res.ok) return { ok: false, error: res.error ?? "Commit failed" };
    return { ok: true };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : String(err) };
  }
}

export async function runDiscard(
  api: GitSetupApi,
): Promise<{ ok: boolean; error?: string }> {
  if (!api.discard) return { ok: false, error: "Discard API not available" };
  try {
    const res = await api.discard();
    if (!res.ok) return { ok: false, error: res.error ?? "Discard failed" };
    return { ok: true };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : String(err) };
  }
}



/** Result of one setup action: always the freshly re-checked readiness, never an assumption. */
export interface GitSetupOutcome {
  readiness: GitReadiness;
  /** Prepare/Baseline found no Git identity: ask for name/email, then retry with `identity`. */
  needsIdentity?: boolean;
  /** The install request's result (plan/command to show when it could not be launched). */
  install?: GitInstallResult;
}

export async function runInstall(api: GitSetupApi): Promise<GitSetupOutcome> {
  let install: GitInstallResult | undefined;
  try {
    install = await api.install();
  } catch {
    install = undefined;
  }
  // The native installer is asynchronous: re-check now, the dialog offers "Check again" afterwards.
  return { readiness: await safeCheck(api), ...(install ? { install } : {}) };
}

export async function runPrepare(api: GitSetupApi, identity?: GitIdentity): Promise<GitSetupOutcome> {
  try {
    const res = await api.prepare(identity ? { identity } : {});
    if (res.needsIdentity) return { readiness: res.readiness, needsIdentity: true };
    return { readiness: await safeCheck(api) };
  } catch {
    return { readiness: { state: "ERROR", message: "Git setup could not be completed." } };
  }
}

/**
 * Create / Add Project: should the Git Setup notice be shown after the Project was opened?
 * Never blocks entering the Project: it is only a notice.
 */
export const needsGitNotice = (git: GitReadiness | undefined): git is GitReadiness =>
  !!git && git.state !== "READY";
